import React, { useState } from "react";
import { NavItemButton, NavSubButton, ProfileChip, WestyMark } from "../ui";
import { AddDisciplineMenu } from "../AddDisciplineMenu";
import { ICONS, buildNav, NavPersona, NavData } from "@/lib/nav";
import { useUI } from "@/lib/theme";
import pkg from "../../../package.json";

export type SidebarSection =
  | "entrenamiento"
  | "salud"
  | "calendario"
  | "coach"
  | "red";

export type SidebarSub = string;

export function Sidebar({
  persona,
  navData,
  section,
  sub,
  collapsed,
  onToggleCollapse,
  onNavigate,
  onSettingsOpen,
  onLogout,
  name,
  role,
  avatar,
  minorityDisc = [],
  discCounts = new Map(),
  addedDisc = [],
  onToggleDisc,
  onHome,
}: {
  persona: NavPersona;
  navData: NavData;
  section: SidebarSection;
  sub: SidebarSub;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onNavigate: (section: SidebarSection, sub: SidebarSub) => void;
  onSettingsOpen: () => void;
  onLogout: () => void;
  name: string;
  role: string;
  avatar?: string;
  minorityDisc?: string[];
  discCounts?: Map<string, number>;
  addedDisc?: string[];
  onToggleDisc?: (c: string) => void;
  onHome?: () => void;
}) {
  const { t } = useUI();
  const groups = buildNav(persona, navData);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set([section]));

  const toggleExpand = (key: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleItemClick = (
    itemKey: string,
    itemAccent: string,
    hasSubs: boolean,
    defaultSub: string
  ) => {
    if (hasSubs) {
      toggleExpand(itemKey);
      onNavigate(itemKey as SidebarSection, sub.startsWith(itemKey) ? sub : defaultSub);
    } else {
      onNavigate(itemKey as SidebarSection, "");
    }
  };

  return (
    <aside
      className={`flex h-full flex-col border-r border-line bg-panel transition-[width] duration-[260ms] cubic-bezier(.4,0,.2,1) ${
        collapsed ? "w-[74px]" : "w-[248px]"
      }`}
    >
      {/* Header */}
      {collapsed ? (
        /* Colapsado: logo (home) + chevron expand */
        <div className="flex flex-col items-center border-b border-line px-3 py-3 gap-1">
          <button
            onClick={() => { onHome?.(); onToggleCollapse(); }}
            title="Home"
            className="rounded-[28%] transition hover:opacity-80"
          >
            <WestyMark className="h-9 w-9 shrink-0" />
          </button>
          <button
            onClick={onToggleCollapse}
            title={t("nav_expand")}
            aria-label={t("nav_expand")}
            className="flex h-5 w-5 items-center justify-center rounded text-muted transition hover:bg-panel2 hover:text-fg"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      ) : (
        /* Expandido: logo (colapsa+home) + título + chevron colapsar */
        <div className="flex items-center gap-2.5 border-b border-line px-3 py-3">
          <button
            onClick={() => { onHome?.(); onToggleCollapse(); }}
            title="Home"
            className="rounded-[28%] transition hover:opacity-80 shrink-0"
          >
            <WestyMark className="h-9 w-9" />
          </button>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-[15px] font-bold tracking-tight text-fg">Westive</span>
            <span className="text-[10px] font-semibold text-muted">Beta(v{pkg.version})</span>
          </div>
          <button
            onClick={onToggleCollapse}
            title={t("nav_collapse")}
            aria-label={t("nav_collapse")}
            className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted transition hover:bg-panel2 hover:text-fg"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 18l-6-6 6-6" /></svg>
          </button>
        </div>
      )}

      {/* Navigation body */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {groups.map((group) => (
          <div key={group.key}>
            {!collapsed && (
              <p className="mb-1 px-2.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-muted">
                {t(group.labelKey)}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const isItemActive = section === item.key;
                const expanded = expandedItems.has(item.key);
                const hasSubs = (item.subs?.length ?? 0) > 0;
                const defaultSub = item.subs?.[0]?.key ?? "";

                return (
                  <div key={item.key}>
                    <NavItemButton
                      icon={ICONS[item.icon] ?? ""}
                      label={t(item.labelKey)}
                      active={isItemActive}
                      badge={item.badge}
                      collapsed={collapsed}
                      accentRgb={item.accent}
                      hasChildren={hasSubs && !collapsed}
                      expanded={expanded}
                      onClick={() =>
                        handleItemClick(item.key, item.accent, hasSubs, defaultSub)
                      }
                    />
                    {/* Sub-items */}
                    {hasSubs && !collapsed && expanded && (
                      <div className="ml-4 mt-0.5 space-y-0.5 border-l border-line pl-2">
                        {item.subs!.map((s) => (
                          <NavSubButton
                            key={s.key}
                            label={t(s.labelKey)}
                            active={isItemActive && sub === s.key}
                            accentRgb={s.accent}
                            onClick={() => onNavigate(item.key as SidebarSection, s.key)}
                          />
                        ))}
                        {item.key === "entrenamiento" && minorityDisc.length > 0 && onToggleDisc && (
                          <AddDisciplineMenu
                            minority={minorityDisc}
                            counts={discCounts}
                            added={addedDisc}
                            onToggle={onToggleDisc}
                            variant="sidebar"
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-line px-2 py-2 space-y-1">
        {/* Settings */}
        <NavItemButton
          icon={ICONS["ajustes"] ?? ""}
          label={t("nav_settings")}
          active={false}
          collapsed={collapsed}
          accentRgb="123 118 107"
          onClick={onSettingsOpen}
        />
        {/* Profile chip */}
        <div className={`py-1.5 ${collapsed ? "flex justify-center" : "px-2"}`}>
          <ProfileChip name={name} role={role} avatar={avatar} collapsed={collapsed} />
        </div>
      </div>
    </aside>
  );
}
